import React, { useEffect, useRef, useState } from "react";
import { View, StyleSheet, Animated, Easing } from "react-native";
import NtText from "../../components/NtText";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtBrandSwitch from "./NtBrandSwitch";
import NtSidebarPlanCard from "../../plans/components/NtSidebarPlanCard";
import { useTheme } from "../../context/ThemeContext";

/**
 * Sidebar — flat nav with section labels and a magnetic active pill.
 *
 * The user card at the top (`NtBrandSwitch`) carries identity + theme toggle
 * + sign-out, so the sidebar itself is just the brand band + nav list.
 *
 * Props:
 *   items: Array of:
 *     { type: 'label', text: 'Operations' }
 *     { type: 'item', key, label, icon, badge?, onPress, isActive?, visible? }
 */
function NtSidebar({ items = [] }) {
  const { theme } = useTheme();
  const styles = makeStyles(theme);

  const positions = useRef({}); // itemKey -> { y, height }
  const pillY = useRef(new Animated.Value(0)).current;
  const pillH = useRef(new Animated.Value(0)).current;
  const pillOpacity = useRef(new Animated.Value(0)).current;

  const [, forceRerender] = useState(0);

  // Find active item
  const activeItem = items.find((i) => i.type === "item" && i.isActive && i.isActive());
  const activeKey = activeItem?.key;

  // Move the pill when the active key changes (and positions are known).
  // Smooth ease-out-cubic matches the design's transition — no bounce,
  // because overshoot makes the pill look mis-aligned for a beat.
  useEffect(() => {
    if (!activeKey) {
      Animated.timing(pillOpacity, {
        toValue: 0, duration: 120, useNativeDriver: false,
      }).start();
      return;
    }
    const pos = positions.current[activeKey];
    if (!pos) return;

    Animated.parallel([
      Animated.timing(pillY, {
        toValue: pos.y,
        duration: 320,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }),
      Animated.timing(pillH, {
        toValue: pos.height,
        duration: 320,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }),
      Animated.timing(pillOpacity, {
        toValue: 1,
        duration: 160,
        useNativeDriver: false,
      }),
    ]).start();
  }, [activeKey]);

  const handleItemLayout = (key) => (e) => {
    const { y, height } = e.nativeEvent.layout;
    positions.current[key] = { y, height };
    // If this is the active item, snap the pill to the fresh layout so
    // the first paint doesn't show the pill in a stale position.
    if (key === activeKey) {
      pillY.setValue(y);
      pillH.setValue(height);
      pillOpacity.setValue(1);
      forceRerender((n) => n + 1);
    }
  };

  return (
    <View style={styles.root}>
      <NtBrandSwitch />

      <View style={styles.navList}>
        <Animated.View
          pointerEvents="none"
          style={[
            styles.pill,
            {
              transform: [{ translateY: pillY }],
              height: pillH,
              opacity: pillOpacity,
            },
          ]}
        >
          <View style={styles.pillBar} />
        </Animated.View>

        {items.map((item, idx) => {
          if (item.type === "label") {
            return (
              <NtText key={`lbl-${idx}`} style={styles.sectionLabel}>
                {item.text}
              </NtText>
            );
          }
          if (item.visible === false) return null;

          const isActive = item.isActive?.() ?? false;

          // Flashy item — brand-blue fill + glow + bright accent badge.
          // Designed to pull the eye toward the setup wizard CTA without any
          // motion (user found the pulsing distracting).
          if (item.flashy) {
            return (
              <NtTouchableEffect
                key={item.key}
                onPress={item.onPress}
                onLayout={handleItemLayout(item.key)}
                style={[styles.navItem, styles.flashyItem]}
              >
                {item.icon ? (
                  <NtMaterialIcon name={item.icon} color="#ffffff" size={14} />
                ) : null}
                <NtText
                  style={[styles.navLabel, styles.flashyLabel]}
                  numberOfLines={1}
                >
                  {item.label}
                </NtText>
                {item.badge ? (
                  <View style={styles.flashyBadge}>
                    <NtText style={styles.flashyBadgeText}>{item.badge}</NtText>
                  </View>
                ) : null}
              </NtTouchableEffect>
            );
          }

          return (
            <NtTouchableEffect
              key={item.key}
              onPress={item.onPress}
              onLayout={handleItemLayout(item.key)}
              style={styles.navItem}
            >
              {item.icon ? (
                <NtMaterialIcon
                  name={item.icon}
                  color={isActive ? theme.brand2 : theme.textDim}
                  size={14}
                />
              ) : null}
              <NtText
                style={[
                  styles.navLabel,
                  { color: isActive ? theme.text : theme.textDim, fontWeight: isActive ? "600" : "500" },
                ]}
                numberOfLines={1}
              >
                {item.label}
              </NtText>
              {item.badge ? (
                <View
                  style={[
                    styles.badge,
                    item.badgeType === "alert" && { backgroundColor: `${theme.red}22` },
                  ]}
                >
                  <NtText
                    style={[
                      styles.badgeText,
                      item.badgeType === "alert" && { color: theme.red },
                    ]}
                  >
                    {item.badge}
                  </NtText>
                </View>
              ) : null}
            </NtTouchableEffect>
          );
        })}
      </View>

      <NtSidebarPlanCard />
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    root: { flex: 1, flexDirection: "column" },
    navList: { flex: 1, paddingHorizontal: 8, paddingTop: 4, paddingBottom: 12, position: "relative" },

    pill: {
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      borderRadius: 6,
    },
    pillBar: {
      position: "absolute",
      left: 2,
      top: "50%",
      marginTop: -8,
      width: 2,
      height: 16,
      borderRadius: 2,
      backgroundColor: t.pillBar,
    },

    sectionLabel: {
      fontSize: 10,
      color: t.textMute,
      fontWeight: "600",
      letterSpacing: 1.2,
      textTransform: "uppercase",
      paddingHorizontal: 12,
      paddingTop: 14,
      paddingBottom: 6,
    },

    navItem: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 10,
      borderRadius: 6,
      height: 34,
      marginBottom: 1,
    },
    navLabel: {
      flex: 1,
      fontSize: 12.5,
      marginLeft: 10,
    },

    badge: {
      minWidth: 18,
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 10,
      backgroundColor: t.surface3,
      alignItems: "center",
      justifyContent: "center",
    },
    badgeText: {
      fontSize: 10,
      color: t.textDim,
      fontWeight: "600",
    },

    // Flashy item — bright brand fill, raised look, accent badge. Pairs with
    // the pulsing scale animation in the component.
    flashyItem: {
      backgroundColor: t.brand,
      borderWidth: 1,
      borderColor: t.brand2,
      marginTop: 4,
      marginBottom: 6,
      shadowColor: t.brand2,
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.55,
      shadowRadius: 10,
    },
    flashyLabel: {
      color: "#ffffff",
      fontWeight: "700",
    },
    flashyBadge: {
      minWidth: 18,
      paddingHorizontal: 7,
      paddingVertical: 2,
      borderRadius: 999,
      backgroundColor: "#ffffff",
      alignItems: "center",
      justifyContent: "center",
    },
    flashyBadgeText: {
      fontSize: 9,
      color: t.brand,
      fontWeight: "800",
      letterSpacing: 0.4,
    },
  });

export default NtSidebar;
